function featureCollection(features) {
  return {
    type: "FeatureCollection",
    features,
  };
}

function classifyBuilding(tags = {}) {
  if (tags.amenity === "clinic" || tags.amenity === "hospital" || tags.amenity === "pharmacy") {
    return { id: "medical", label: "Medical site" };
  }
  if (tags.shop || ["fast_food", "restaurant", "cafe", "bar", "pub"].includes(tags.amenity)) {
    return { id: "retail", label: "Shop" };
  }
  if (tags.amenity === "school" || tags.building === "school") {
    return { id: "civic", label: "School" };
  }
  if (tags.amenity === "place_of_worship" || tags.building === "church" || tags.building === "chapel") {
    return { id: "civic", label: "Civic building" };
  }
  if (["warehouse", "industrial", "commercial", "retail", "supermarket"].includes(tags.building)) {
    return { id: "commercial", label: "Commercial building" };
  }
  if (["garage", "garages"].includes(tags.building)) {
    return { id: "utility", label: "Garage" };
  }
  if (["house", "detached", "semidetached_house", "terrace", "residential", "apartments"].includes(tags.building)) {
    return { id: "residential", label: "Residence" };
  }
  return { id: "unknown", label: "Building" };
}

function buildingPalette(kind) {
  const palettes = {
    residential: { fill: "#8ea56f", outline: "#4d6540" },
    commercial: { fill: "#c28f58", outline: "#76512f" },
    retail: { fill: "#d49b64", outline: "#7a5537" },
    medical: { fill: "#c96d73", outline: "#783c42" },
    civic: { fill: "#8b8abf", outline: "#4d4f7e" },
    utility: { fill: "#9c9484", outline: "#5b554b" },
    unknown: { fill: "#9aa196", outline: "#5d665f" },
  };
  return palettes[kind.id] || palettes.unknown;
}

function stableHash(value) {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

export const DESTROYED_BUILDING_RATE = 0.30;
export const DESTROYED_RESIDENTIAL_BUILDING_RATE = DESTROYED_BUILDING_RATE;

function buildingEligibleForDamage(kindId, { named = false } = {}) {
  if (kindId === "residential") return true;
  if (kindId === "commercial" || kindId === "retail") return false;
  return !named;
}

export function buildingDamageProfile(elementType, osmId, kindId, {
  worldSeed = "",
  destroyedRate = DESTROYED_BUILDING_RATE,
  named = false,
} = {}) {
  if (!buildingEligibleForDamage(kindId, { named })) return intactBuildingDamageProfile();
  if (!worldSeed || !destroyedRate) return intactBuildingDamageProfile();
  const boundedRate = Math.max(0, Math.min(1, Number(destroyedRate) || 0));
  const roll = stableHash(`${worldSeed}:${elementType}:${osmId}:${kindId}:damage`) / 0x100000000;
  if (roll < boundedRate) {
    return {
      state: "destroyed",
      label: "Destroyed",
      color: "#4a443c",
      outlineColor: "#a7815a",
    };
  }
  return intactBuildingDamageProfile();
}

function intactBuildingDamageProfile() {
  return {
    state: "none",
    label: "Intact",
    color: "",
    outlineColor: "",
  };
}

function formatBuildingAddress(tags) {
  const firstLine = [tags["addr:housenumber"], tags["addr:street"]].filter(Boolean).join(" ");
  return [firstLine, tags["addr:city"]].filter(Boolean).join(", ");
}

function buildingProperties(element) {
  const tags = element.tags || {};
  const kind = classifyBuilding(tags);
  const palette = buildingPalette(kind);
  const damage = intactBuildingDamageProfile();
  const explicitName = Boolean(tags.name || tags.brand);
  return {
    osmId: String(element.id),
    elementType: element.type,
    name: tags.name || tags.brand || kind.label,
    building: tags.building || "",
    shop: tags.shop || "",
    amenity: tags.amenity || "",
    address: formatBuildingAddress(tags),
    levels: tags["building:levels"] || "",
    height: tags.height || "",
    source: tags.source || "",
    tagCount: Object.keys(tags).length,
    rawTags: Object.keys(tags).sort().map((key) => `${key}=${tags[key]}`),
    explicitName,
    kind: kind.id,
    kindLabel: kind.label,
    color: palette.fill,
    outlineColor: palette.outline,
    damageState: damage.state,
    damageLabel: damage.label,
    damageColor: damage.color,
    damageOutlineColor: damage.outlineColor,
  };
}

export function withBuildingAtmosphereProperties(feature, options = {}) {
  const properties = feature?.properties || {};
  if (properties.damageState && !options.worldSeed) return feature;
  const kindId = properties.kind || "unknown";
  const named = properties.explicitName === true
    || (Boolean(properties.name) && properties.name !== properties.kindLabel && properties.name !== "Building");
  const damage = buildingDamageProfile(properties.elementType || "osm", properties.osmId || properties.id || "", kindId, {
    ...options,
    named,
  });
  return {
    ...feature,
    properties: {
      ...properties,
      damageState: damage.state,
      damageLabel: damage.label,
      damageColor: damage.color,
      damageOutlineColor: damage.outlineColor,
    },
  };
}

export function normalizeBuildingFeatureCollection(collection, options = {}) {
  return featureCollection((collection?.features || []).map((feature) => withBuildingAtmosphereProperties(feature, options)));
}

function closedRingFromGeometry(geometry) {
  if (!Array.isArray(geometry) || geometry.length < 3) return null;
  const ring = geometry
    .map((point) => [Number(point.lon), Number(point.lat)])
    .filter((coordinate) => coordinate.every(Number.isFinite));
  if (ring.length < 3) return null;
  const first = ring[0];
  const last = ring[ring.length - 1];
  if (first[0] !== last[0] || first[1] !== last[1]) ring.push([...first]);
  return ring.length >= 4 ? ring : null;
}

function relationOuterPolygons(element) {
  const polygons = [];
  for (const member of element.members || []) {
    if (member.type !== "way" || member.role && member.role !== "outer") continue;
    const ring = closedRingFromGeometry(member.geometry);
    if (ring) polygons.push([ring]);
  }
  return polygons;
}

export function buildingDataToGeoJSON(overpassData, options = {}) {
  const features = [];
  for (const element of overpassData.elements || []) {
    if (element.type === "way") {
      const ring = closedRingFromGeometry(element.geometry);
      if (!ring) continue;
      features.push(withBuildingAtmosphereProperties({
        type: "Feature",
        id: `way-${element.id}`,
        geometry: { type: "Polygon", coordinates: [ring] },
        properties: buildingProperties(element),
      }, options));
    }
    if (element.type === "relation") {
      const polygons = relationOuterPolygons(element);
      if (!polygons.length) continue;
      features.push(withBuildingAtmosphereProperties({
        type: "Feature",
        id: `relation-${element.id}`,
        geometry: { type: "MultiPolygon", coordinates: polygons },
        properties: buildingProperties(element),
      }, options));
    }
  }
  return featureCollection(features);
}
